import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { cn } from '../utils/cn';

// =============================================================================
// 전역 토스트 — 작업 성공/실패를 하단 중앙에서 등장·사라짐 애니메이션과 함께 알린다.
// 앱 어디서나 toast()로 띄우고, ToastHost는 App에 한 번 마운트한다.
// describeApiError로 API Error("<status> <text>")를 한국어로 변환한다.
// =============================================================================

type ToastKind = 'error' | 'success' | 'info';
type ToastItem = { id: number; message: string; kind: ToastKind; leaving?: boolean };

type ToastStore = {
  items: ToastItem[];
  push: (message: string, kind: ToastKind) => void;
  dismiss: (id: number) => void;
};

let seq = 0;
// 사라짐 애니메이션 시간(ms) — leaving 표시 후 이 시간 뒤 실제 제거.
const LEAVE_MS = 220;

const useToastStore = create<ToastStore>((set) => {
  const beginLeave = (id: number): void => {
    set((s) => ({ items: s.items.map((t) => (t.id === id ? { ...t, leaving: true } : t)) }));
    setTimeout(() => set((s) => ({ items: s.items.filter((t) => t.id !== id) })), LEAVE_MS);
  };
  return {
    items: [],
    push: (message, kind) => {
      const id = ++seq;
      set((s) => ({ items: [...s.items, { id, message, kind }] }));
      // 긴 안내(예: 슬라이드 저장 뒤 반영 시간, SCDS-116)는 읽을 시간만큼 더 둔다 — 한 글자에 70ms.
      const ttl = Math.max(kind === 'error' ? 6000 : 3500, message.length * 70);
      setTimeout(() => beginLeave(id), ttl);
    },
    dismiss: beginLeave,
  };
});

/** 앱 어디서나 호출 가능한 전역 토스트. */
export function toast(message: string, kind: ToastKind = 'info'): void {
  useToastStore.getState().push(message, kind);
}

/**
 * API 에러를 사용자용 한국어 문구로 변환한다. 서버가 사용자용 detail을 준 경우
 * 상태 코드의 일반 문구보다 먼저 보여주고, 없을 때만 상태별 안내로 대체한다.
 */
/** 에러 메시지에 섞여 온 JSON 응답에서 사용자용 `detail` 만 뽑는다. */
function detailOf(raw: string): string | null {
  const start = raw.indexOf('{');
  if (start < 0) return null;
  try {
    const parsed = JSON.parse(raw.slice(start)) as { detail?: unknown };
    return typeof parsed.detail === 'string' && parsed.detail.trim() ? parsed.detail : null;
  } catch {
    return null;
  }
}

function statusOf(raw: string): number | null {
  const match = raw.match(/^(?:HTTP\s+)?(\d{3})\b/i) ?? raw.match(/\bHTTP\s+(\d{3})\b/i);
  return match ? Number(match[1]) : null;
}

// api.ts의 ApiError를 import 하지 않고 모양으로 판별한다. 화면 테스트가
// client/api 를 부분 mock 해도 이 문구 변환이 깨지지 않아야 하고, 표시 헬퍼가
// 클라이언트 모듈의 클래스 identity에 묶이지 않는 편이 안전하다.
type ApiErrorShape = { status: number; code: string | null; detail: string | null };
function apiErrorShapeOf(e: unknown): ApiErrorShape | null {
  if (typeof e !== 'object' || e === null) return null;
  const candidate = e as Partial<ApiErrorShape>;
  if (typeof candidate.status !== 'number') return null;
  const code = candidate.code ?? null;
  const detail = candidate.detail ?? null;
  if (code !== null && typeof code !== 'string') return null;
  if (detail !== null && typeof detail !== 'string') return null;
  return { status: candidate.status, code, detail };
}

export function describeApiError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  const apiError = apiErrorShapeOf(e);
  const detail = apiError?.detail?.trim() || detailOf(raw);
  if (detail) return detail;

  const status = apiError?.status ?? statusOf(raw);
  if (status === 400) return '요청 내용을 확인한 뒤 다시 시도해 주세요.';
  if (status === 401) return '로그인이 필요합니다. 다시 로그인해 주세요.';
  if (status === 403) return '권한이 없어 요청을 처리할 수 없습니다. (관리자 문의)';
  if (status === 404) return '대상을 찾을 수 없습니다. 새로고침 후 다시 시도해 주세요.';
  if (status === 409) return '다른 변경과 충돌했습니다. 새로고침 후 다시 시도해 주세요.';
  if (status === 423 || apiError?.code === 'org_pending_deletion' || /org_pending_deletion/.test(raw))
    return '삭제 대기 중인 조직입니다. 수정할 수 없습니다.';
  if (status !== null && status >= 500 && status < 600)
    return '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
  if (/network\s*error|failed to fetch|load failed/i.test(raw))
    return '네트워크 오류입니다. 연결 상태를 확인해 주세요.';
  return raw || '알 수 없는 오류가 발생했습니다.';
}

export function ToastHost(): JSX.Element {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);
  return createPortal(
    <div className="fixed bottom-6 max-md:bottom-[calc(4.5rem+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-[100000] flex flex-col items-center gap-2 max-w-[calc(100vw-2rem)] pointer-events-none">
      {items.map((t) => (
        <ToastRow key={t.id} item={t} onClose={() => dismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}

// 개별 토스트 행 — 마운트 시 아래에서 떠오르며 등장, leaving 시 반대로 사라짐.
function ToastRow({ item, onClose }: { item: ToastItem; onClose: () => void }): JSX.Element {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, []);
  const visible = entered && !item.leaving;
  return (
    <button
      type="button"
      onClick={onClose}
      className={cn(
        // Figma Toast Popup Layout — 최소 너비 320, 최대 너비 580.
        'pointer-events-auto flex items-center gap-2.5 px-4 py-3 rounded-lg border border-line bg-panel text-text shadow-lg text-[14px] leading-snug',
        'min-w-[320px] max-w-[580px] w-fit',
        'transition-all duration-200 ease-out',
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
      )}
    >
      <ToastIcon kind={item.kind} />
      <span className="text-left">{item.message}</span>
    </button>
  );
}

/** Figma Toast Case — 오류는 빨간 원 ×, 성공은 초록 원 ✓. */
function ToastIcon({ kind }: { kind: ToastKind }): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold leading-none text-white',
        kind === 'success' && 'bg-green',
        kind === 'error' && 'bg-red',
        kind === 'info' && 'bg-accent',
      )}
      aria-hidden="true"
    >
      {kind === 'success' ? '✓' : kind === 'error' ? '×' : 'i'}
    </span>
  );
}
